import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Trash2, 
  Crown, 
  Shield, 
  User,
  Clock,
  Settings,
  Eye,
  AlertCircle
} from "lucide-react";
import { OnboardingData } from "../../business/CreateBusinessTab";
import { useRoles } from "@/hooks/employees/useRoles";

interface TeamSetupStepProps {
  onboardingData: OnboardingData;
  setOnboardingData: React.Dispatch<React.SetStateAction<OnboardingData>>;
  businessId?: string; // Optional business ID for dynamic roles
}

// Default role definitions for fallback when dynamic roles aren't available
const DEFAULT_ROLE_DEFINITIONS = [
  {
    name: "Manager",
    description: "Full access to business management and settings",
    icon: Crown,
    permissions: ["Manage employees", "View all data", "Edit settings", "Financial access"],
    color: "bg-primary/10 text-primary"
  },
  {
    name: "Employee", 
    description: "Basic employee access for daily operations",
    icon: User,
    permissions: ["Clock in/out", "View own schedule", "Basic customer interaction"],
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
  },
  {
    name: "Shift Manager",
    description: "Manages shifts and employee schedules",
    icon: Clock,
    permissions: ["Manage schedules", "Employee oversight", "Shift reporting"],
    color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
  },
  {
    name: "Administrator",
    description: "Technical admin with system access",
    icon: Settings,
    permissions: ["System settings", "User management", "Technical support"],
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
  },
  {
    name: "Viewer",
    description: "Read-only access to reports and data",
    icon: Eye,
    permissions: ["View reports", "Read-only dashboard", "Basic analytics"],
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
  }
];

const TeamSetupStep: React.FC<TeamSetupStepProps> = ({
  onboardingData,
  setOnboardingData,
  businessId,
}) => {
  const { data: roles } = useRoles(businessId || "");
  const [employeeRows, setEmployeeRows] = useState([
    { name: "", email: "", role_id: "" }
  ]);

  // Get available roles (either from database or default fallback)
  const availableRoles = roles?.length ? roles.map(role => ({
    id: role.id,
    name: role.name,
    description: role.description || "",
    icon: User, // Default icon for dynamic roles
    permissions: [], // Dynamic roles don't have predefined permissions in this context
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
  })) : DEFAULT_ROLE_DEFINITIONS.map((role, index) => ({
    id: role.name.toLowerCase().replace(" ", "_"),
    name: role.name,
    description: role.description,
    icon: role.icon,
    permissions: role.permissions,
    color: role.color
  }));

  const handleAddEmployeeRow = () => {
    const defaultRoleId = availableRoles.find(r => r.name.toLowerCase() === "employee")?.id || availableRoles[0]?.id || "";
    setEmployeeRows(prev => [...prev, { name: "", email: "", role_id: defaultRoleId }]);
  };

  const handleRemoveEmployeeRow = (index: number) => {
    if (employeeRows.length > 1) {
      setEmployeeRows(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleEmployeeChange = (index: number, field: string, value: string) => {
    setEmployeeRows(prev => 
      prev.map((employee, i) => 
        i === index ? { ...employee, [field]: value } : employee
      )
    );
  };

  const handleAddAllEmployees = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const validEmployees = employeeRows.filter(employee => 
      employee.name.trim() && 
      employee.email.trim() && 
      emailRegex.test(employee.email)
    );

    if (validEmployees.length === 0) {
      return;
    }

    setOnboardingData(prev => ({
      ...prev,
      teamMembers: [...prev.teamMembers, ...validEmployees]
    }));

    // Reset to single empty row
    const defaultRoleId = availableRoles.find(r => r.name.toLowerCase() === "employee")?.id || availableRoles[0]?.id || "";
    setEmployeeRows([{ name: "", email: "", role_id: defaultRoleId }]);
  };

  const handleRemoveMember = (index: number) => {
    setOnboardingData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-4">
      {/* Role Definitions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>Available Roles</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableRoles.map((role) => {
              const Icon = role.icon;
              return (
                <div key={role.id} className="p-3 border rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`p-1.5 rounded-lg ${role.color}`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">{role.name}</h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{role.description}</p>
                    </div>
                  </div>
                  {role.permissions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {role.permissions.slice(0, 2).map((permission, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {permission}
                        </Badge>
                      ))}
                      {role.permissions.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{role.permissions.length - 2} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add Team Members */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <UserPlus className="w-5 h-5" />
            <span>Add Team Members</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {employeeRows.map((employee, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border rounded-lg">
                <div>
                  <Label htmlFor={`member-name-${index}`}>Full Name</Label>
                  <Input
                    id={`member-name-${index}`}
                    placeholder="John Doe"
                    value={employee.name}
                    onChange={(e) => handleEmployeeChange(index, 'name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`member-email-${index}`}>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      id={`member-email-${index}`}
                      type="email"
                      placeholder="john@example.com"
                      className="pl-10"
                      value={employee.email}
                      onChange={(e) => handleEmployeeChange(index, 'email', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor={`member-role-${index}`}>Role</Label>
                  <Select
                    value={employee.role_id}
                    onValueChange={(value) => handleEmployeeChange(index, 'role_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((role) => {
                        const Icon = role.icon;
                        return (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center space-x-2">
                              <Icon className="w-4 h-4" />
                              <span>{role.name}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddEmployeeRow}
                    className="flex items-center space-x-1"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Add</span>
                  </Button>
                  {employeeRows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmployeeRow(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button
              onClick={handleAddAllEmployees}
              disabled={!employeeRows.some(emp => emp.name.trim() && emp.email.trim())}
              className="flex items-center space-x-2"
            >
              <Users className="w-4 h-4" />
              <span>Add All Employees</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Team Members List */}
      {onboardingData.teamMembers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>Team Members ({onboardingData.teamMembers.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {onboardingData.teamMembers.map((member, index) => {
                const roleInfo = availableRoles.find(r => r.id === member.role_id);
                const RoleIcon = roleInfo?.icon || User;
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${roleInfo?.color || "bg-gray-100"}`}>
                        <RoleIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium">{member.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{member.email}</p>
                      </div>
                      <Badge className={roleInfo?.color || "bg-gray-100"}>
                        {roleInfo?.name || member.role_id}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information Card */}
      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/10">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-1">Team member invitations</p>
              <p>
                Team members will receive email invitations to join your business account. 
                They can accept the invitation and set up their own login credentials.
                You can always add more team members later from the dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamSetupStep;